import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  BookOpen,
  Headphones,
  Eye,
  Clock,
  TrendingUp,
} from "lucide-react";
import {
  fetchUserRecommendations,
  BookRecommendation,
  UserBehaviorData,
} from "@/api/recommendations";

interface UserRecommendationsProps {
  userId: number;
  userName: string;
}

interface RecommendationCardProps {
  recommendation: BookRecommendation;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
}) => {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex gap-3">
          {/* Book Cover */}
          <div className="flex-shrink-0">
            <div
              className="w-16 h-24 rounded-md flex items-center justify-center text-white text-xs font-medium"
              style={{
                backgroundColor: recommendation.cover_bg_color || "#6B7280",
                backgroundImage: recommendation.cover_image
                  ? `url(${recommendation.cover_image})`
                  : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {!recommendation.cover_image && <BookOpen className="h-6 w-6" />}
            </div>
          </div>

          {/* Book Details */}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm line-clamp-2 mb-2">
              {recommendation.title}
            </h4>

            {/* Authors */}
            {recommendation.authors && recommendation.authors.length > 0 && (
              <p className="text-xs text-muted-foreground mb-1">
                by{" "}
                {recommendation.authors.map((author) => author.name).join(", ")}
              </p>
            )}

            {/* Categories */}
            {recommendation.categories &&
              recommendation.categories.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {recommendation.categories.slice(0, 2).map((category) => (
                    <Badge
                      key={category.id}
                      variant="secondary"
                      className="text-xs"
                    >
                      {category.name}
                    </Badge>
                  ))}
                  {recommendation.categories.length > 2 && (
                    <span className="text-xs text-muted-foreground">
                      +{recommendation.categories.length - 2} more
                    </span>
                  )}
                </div>
              )}

            {/* Book Stats */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
              {recommendation.reading_time && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {recommendation.reading_time} min
                </div>
              )}
              {recommendation.rating && (
                <div className="flex items-center gap-1">
                  <span>⭐</span>
                  {recommendation.rating.toFixed(1)}
                </div>
              )}
              {recommendation.view_count && (
                <div className="flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  {recommendation.view_count}
                </div>
              )}
            </div>

            {/* Recommendation Score */}
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-3 w-3 text-primary" />
              <span className="text-xs font-medium text-primary">
                Score: {recommendation.recommendation_score.toFixed(1)}
              </span>
            </div>

            {/* Recommendation Reasons */}
            {recommendation.recommendation_reasons.length > 0 && (
              <div className="text-xs text-muted-foreground">
                <p className="font-medium mb-1">Why recommended:</p>
                <ul className="space-y-0.5">
                  {recommendation.recommendation_reasons
                    .slice(0, 2)
                    .map((reason, index) => (
                      <li key={index} className="text-xs">
                        • {reason}
                      </li>
                    ))}
                  {recommendation.recommendation_reasons.length > 2 && (
                    <li className="text-xs italic">
                      +{recommendation.recommendation_reasons.length - 2} more
                      reasons
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const UserBehaviorSummary: React.FC<{ userBehavior: UserBehaviorData }> = ({
  userBehavior,
}) => {
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">User Behavior Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <Eye className="h-5 w-5 text-blue-500" />
            </div>
            <div className="text-2xl font-bold">{userBehavior.total_views}</div>
            <div className="text-sm text-muted-foreground">Book Views</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <BookOpen className="h-5 w-5 text-green-500" />
            </div>
            <div className="text-2xl font-bold">{userBehavior.total_reads}</div>
            <div className="text-sm text-muted-foreground">
              Reading Sessions
            </div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <Headphones className="h-5 w-5 text-purple-500" />
            </div>
            <div className="text-2xl font-bold">
              {userBehavior.total_audio_plays}
            </div>
            <div className="text-sm text-muted-foreground">Audio Plays</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <Clock className="h-5 w-5 text-orange-500" />
            </div>
            <div className="text-2xl font-bold">
              {formatTime(userBehavior.total_reading_time)}
            </div>
            <div className="text-sm text-muted-foreground">
              Total Reading Time
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium">Favorite Categories: </span>
              <span className="text-muted-foreground">
                {userBehavior.favorite_categories.length} selected
              </span>
            </div>
            <div>
              <span className="font-medium">Recent Books: </span>
              <span className="text-muted-foreground">
                {userBehavior.recent_books.length} in last 30 days
              </span>
            </div>
            <div>
              <span className="font-medium">Completed Books: </span>
              <span className="text-muted-foreground">
                {userBehavior.completed_books.length} finished
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const UserRecommendations: React.FC<UserRecommendationsProps> = ({
  userId,
  userName,
}) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ["userRecommendations", userId],
    queryFn: () => fetchUserRecommendations(userId, 12),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userId,
  });

  const handleRefresh = async () => {
    try {
      console.log("Refreshing recommendations for user:", userId);

      // Invalidate the cache to force a fresh fetch
      await queryClient.invalidateQueries({
        queryKey: ["userRecommendations", userId],
      });

      // Also call refetch as a backup
      await refetch();
      console.log("Recommendations refreshed successfully");
    } catch (error) {
      console.error("Error refreshing recommendations:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Recommendations for {userName}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Recommendations for {userName}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">
                Failed to load recommendations: {error.message}
              </p>
              <button
                onClick={handleRefresh}
                disabled={isRefetching}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isRefetching ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  "Try Again"
                )}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { recommendations, userBehavior, metadata } = data || {
    recommendations: [],
    userBehavior: {
      total_views: 0,
      total_reads: 0,
      total_audio_plays: 0,
      total_reading_time: 0,
      favorite_categories: [],
      recent_books: [],
      completed_books: [],
    },
    metadata: {
      totalBooksAnalyzed: 0,
      userCategoriesCount: 0,
      generatedAt: new Date().toISOString(),
    },
  };

  return (
    <div className="space-y-6">
      {/* User Behavior Summary */}
      <UserBehaviorSummary userBehavior={userBehavior} />

      {/* Recommendations */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Personalized Recommendations for {userName}</CardTitle>
            <button
              onClick={handleRefresh}
              disabled={isRefetching}
              className="px-3 py-1 text-xs bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {isRefetching ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Refreshing...
                </>
              ) : (
                "Refresh"
              )}
            </button>
          </div>
          {metadata && (
            <p className="text-sm text-muted-foreground">
              Analyzed {metadata.totalBooksAnalyzed} books •{" "}
              {metadata.userCategoriesCount} user categories • Generated{" "}
              {new Date(metadata.generatedAt).toLocaleString()}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {recommendations.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium mb-2">No Recommendations Available</h3>
              <p className="text-muted-foreground">
                This user hasn't selected any categories yet or there are no
                books matching their preferences.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recommendations.map((recommendation) => (
                <RecommendationCard
                  key={recommendation.id}
                  recommendation={recommendation}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserRecommendations;
